import {MultiTargetRelation} from '../MultiTargetRelation';
import {SketchRelationKind} from '../../relation-compatibility/SketchRelationKinds';
import type {SketchPointObject} from '../../../../../mesh/sketch/SketchPointObject';

let count = 0;

/**
 * 判断对象是否为三点定角约束。
 *
 * @param value 待判断对象。
 * @returns 是否为三点定角约束。
 */
export function isThreePointAngleConstraint(value: object): value is ThreePointAngleConstraint {
    return Reflect.get(value, 'isThreePointAngleConstraint') === true;
}

/**
 * 三点定角约束（关系视图：`三点定角`）。
 *
 * 对齐 SOLIDWORKS 的 `swConstraintType_ANGLE3P`：由「顶点 + 两条射线端点」三个点确定的角，
 * 圆弧槽口的角度尺寸在 SW 属性栏里正是显示为该关系（顶点=弧心、两射线端点=两端帽圆心）。
 *
 * 目标顺序约定：`[顶点, 射线一端点, 射线二端点]`。
 *
 * 几何维持由参数公式承担，本对象保存锁定角度，旋向由基类稳定分支提示跨求解与历史延续。
 */
export class ThreePointAngleConstraint extends MultiTargetRelation<SketchPointObject> {
    /** 运行时类型标记。 */
    public readonly isThreePointAngleConstraint: boolean = true;

    /** 关系类型。 */
    public relationType = SketchRelationKind.ThreePointAngle;

    /**
     * 锁定角度（弧度，(0, 2π) 的跨角幅值）；未锁定为 null。
     *
     * 允许优角：圆弧槽口常见跨角超过平角，SW 同样标注优角（如 198.27°）。
     * 旋向不混入角度数值，由关系分支提示独立保存；首次无提示时才按当前几何择近。
     */
    private lockedAngleRad: number | null = null;

    /**
     * @param angleRad 锁定角度（弧度，正数且小于 π）；非法值忽略。
     */
    public constructor(angleRad?: number) {
        super();
        this.name = `${this.baseDisplayName}${count}`;
        count += 1;
        if (typeof angleRad === 'number') {
            this.setLockedAngle(angleRad);
        }
    }

    /** 基础显示名。 */
    public get baseDisplayName(): string {
        return '三点定角';
    }

    /**
     * 读取锁定角度。
     *
     * @returns 锁定角度（弧度）；未锁定返回 null。
     */
    public getLockedAngle(): number | null {
        return this.lockedAngleRad;
    }

    /**
     * 覆写锁定角度（尺寸改值时由尺寸侧同步；尺寸与本约束是同一条约束的两个视图）。
     *
     * @param angleRad 新锁定角度（弧度）；非有限值或非正值忽略。
     */
    public setLockedAngle(angleRad: number): void {
        if (!Number.isFinite(angleRad) || angleRad <= 0) {
            return;
        }
        this.lockedAngleRad = angleRad;
    }

}
